import React, { useState, useEffect, useMemo } from 'react';
import {
  MessageSquare, Plus, TrendingUp, Clock, Flame,
  Search, Pin, Lock, Eye, Heart, Star, Sparkles,
  Newspaper, HelpCircle, ChevronRight, Hash, Users,
} from 'lucide-react';
import { forumAPI } from '../services/api';
import { getUserFromToken, isAuthenticated } from '../utils/auth';
import CreateThreadModal from './CreateThreadModal';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

const c = {
  bg: '#0a0a0f',
  card: 'linear-gradient(180deg,#151420,#101018)',
  cardFlat: '#151420',
  surface: '#181722',
  border: '#232130',
  borderSoft: '#2a2836',
  text: '#f4f3f7',
  textSub: '#a8a5b4',
  textMuted: '#8f8b9c',
  textFaint: '#6f6b7d',
  chevron: '#3f3c4a',
  purple: '#8b5cf6',
  purpleLight: '#c4b5fd',
  pink: '#ec4899',
  pinkLight: '#f9a8d4',
  green: '#6ee7b7',
  amber: '#f59e0b',
  gold: '#fbbf24',
};

// icon + tag palette per forum category
const CATEGORY_CFG = {
  discussion:       { label: 'Discussion',       icon: MessageSquare, iconColor: c.purpleLight, tagBg: 'rgba(139,92,246,0.15)', tagColor: c.purpleLight },
  question:         { label: 'Question',         icon: HelpCircle,    iconColor: c.green,       tagBg: 'rgba(52,211,153,0.15)', tagColor: c.green },
  review:           { label: 'Review',           icon: Star,          iconColor: c.gold,        tagBg: 'rgba(251,191,36,0.15)', tagColor: c.gold },
  reaction_request: { label: 'Reaction Request', icon: Sparkles,      iconColor: '#fdba74',     tagBg: 'rgba(249,115,22,0.15)', tagColor: '#fdba74' },
  news:             { label: 'News',             icon: Newspaper,     iconColor: c.pinkLight,   tagBg: 'rgba(236,72,153,0.15)', tagColor: c.pinkLight },
  meme:             { label: 'Meme',             icon: Users,         iconColor: '#93c5fd',     tagBg: 'rgba(59,130,246,0.15)', tagColor: '#93c5fd' },
  other:            { label: 'Other',            icon: Hash,          iconColor: '#c9c6d3',     tagBg: '#2e2a3d',               tagColor: '#c9c6d3' },
};

const CATEGORY_ORDER = ['all', 'discussion', 'question', 'review', 'reaction_request', 'news', 'meme'];

const SORT_FILTERS = [
  { id: 'latest', icon: Clock, label: 'Latest' },
  { id: 'trending', icon: TrendingUp, label: 'Trending' },
  { id: 'popular', icon: Flame, label: 'Popular' },
];

const keyframes = `
@keyframes cf-pulse{0%,100%{opacity:1}50%{opacity:.5}}
@keyframes cf-fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
`;

const cfgFor = (cat) => CATEGORY_CFG[cat] || CATEGORY_CFG.other;

const formatDate = (date) => {
  const diffMs = Date.now() - new Date(date);
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

// ── Thread row ───────────────────────────────────────────────────────────────

const ThreadRow = ({ thread, isHovered, onHover, onOpen, onLike, liked, likeBusy, isMobile }) => {
  const cfg = cfgFor(thread.category);
  const Icon = thread.isPinned ? Pin : cfg.icon;
  const iconColor = thread.isPinned ? c.purpleLight : cfg.iconColor;
  const linked = thread.linkedContent;

  // On phones the stats can't share a row with the title — the cluster is
  // ~200px wide and squeezes the title down to a few characters. Drop it to
  // its own full-width row underneath instead.
  const stats = (
    <div style={{
      display: 'flex', alignItems: 'center',
      gap: isMobile ? 14 : 16,
      flexShrink: 0,
      width: isMobile ? '100%' : undefined,
      marginTop: isMobile ? 10 : 0,
      borderTop: isMobile ? `1px solid ${c.border}` : undefined,
      paddingTop: isMobile ? 10 : 2,
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: c.textSub }}>
        <MessageSquare size={14} /> {compact(thread.stats?.replies)}
      </span>

      <span style={{
        display: 'flex', alignItems: 'center', gap: 5, fontSize: 13,
        color: liked ? c.pink : c.textSub,
      }}>
        <Heart size={14} fill={liked ? c.pink : 'none'} /> {compact(thread.stats?.likes)}
      </span>

      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: c.textFaint }}>
        <Eye size={14} /> {compact(thread.stats?.views)}
      </span>

      <button
        type="button"
        title={liked ? 'Remove like' : 'Like thread'}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike(thread); }}
        disabled={likeBusy}
        style={{
          background: 'none', border: 'none', padding: 4,
          color: liked ? c.pink : c.textFaint,
          cursor: likeBusy ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center',
          transition: 'color 0.15s', opacity: likeBusy ? 0.5 : 1,
          marginLeft: isMobile ? 'auto' : 0,
        }}
      >
        <Heart size={17} fill={liked ? c.pink : 'none'} />
      </button>

      {!isMobile && <ChevronRight size={16} style={{ color: c.chevron }} />}
    </div>
  );

  return (
    <a
      href={`/forum/${thread.slug}`}
      onClick={(e) => { e.preventDefault(); onOpen(thread.slug); }}
      onMouseEnter={() => onHover(thread._id)}
      onMouseLeave={() => onHover(null)}
      style={{
        background: c.card,
        border: `1px solid ${isHovered ? c.purple : thread.isPinned ? 'rgba(251,191,36,0.35)' : c.border}`,
        borderRadius: 16,
        padding: isMobile ? '14px' : '18px 20px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: isMobile ? 0 : 16,
        cursor: 'pointer',
        textDecoration: 'none',
        transform: isHovered && !isMobile ? 'translateY(-3px) scale(1.005)' : 'none',
        boxShadow: isHovered && !isMobile ? '0 16px 36px rgba(139,92,246,0.18)' : 'none',
        transition: 'transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 12 : 16, minWidth: 0, flex: isMobile ? undefined : 1 }}>
        {/* category icon */}
        <div style={{
          width: isMobile ? 36 : 42, height: isMobile ? 36 : 42,
          borderRadius: 12, flexShrink: 0,
          background: c.surface, border: `1px solid ${c.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: iconColor,
        }}>
          <Icon size={isMobile ? 16 : 18} />
        </div>

        {/* body */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {thread._isTrending && (
            <span style={{
              display: 'block', width: 'fit-content',
              background: `linear-gradient(135deg, ${c.amber}, ${c.pink})`,
              color: '#fff', fontSize: 10.5, fontWeight: 700,
              padding: '4px 10px', borderRadius: 999,
              letterSpacing: '0.02em', lineHeight: 1.3, margin: '0 0 8px',
            }}>
              🔥 TRENDING
            </span>
          )}

          <h3 style={{
            fontFamily: SORA, fontWeight: 700,
            fontSize: isMobile ? 15 : 16.5,
            margin: isMobile ? '0 0 8px' : '0 0 10px',
            lineHeight: 1.35, color: c.text,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            {thread.isLocked && <Lock size={14} style={{ color: c.textFaint, flexShrink: 0, marginTop: 3 }} />}
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: isMobile ? 3 : 2, WebkitBoxOrient: 'vertical',
              minWidth: 0,
            }}>
              {thread.title}
            </span>
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: isMobile ? 12 : 13, color: c.textMuted }}>
              {thread.authorUsername || thread.authorId?.username || 'unknown'} · {formatDate(thread.createdAt)}
            </span>
            <span style={{
              background: cfg.tagBg, color: cfg.tagColor,
              fontSize: isMobile ? 11 : 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
            }}>
              {cfg.label}
            </span>

            {linked?.type === 'peak_moment' && (
              <span style={{
                background: 'rgba(249,115,22,0.15)', color: '#fdba74',
                fontSize: isMobile ? 11 : 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
              }}>
                🔥 Peak Moment
              </span>
            )}
            {(linked?.type === 'movie' || linked?.type === 'tv_show') && (
              <span style={{
                background: 'rgba(52,211,153,0.15)', color: c.green,
                fontSize: isMobile ? 11 : 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                maxWidth: isMobile ? 160 : 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {linked.type === 'movie' ? '🎬' : '📺'} {linked.title}
              </span>
            )}
          </div>
        </div>
      </div>

      {stats}
    </a>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────────

const Forum = ({ onThreadClick }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('latest');
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hoveredThread, setHoveredThread] = useState(null);
  const [likeBusyId, setLikeBusyId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentUserId = useMemo(() => {
    if (!isAuthenticated()) return null;
    const u = getUserFromToken();
    return u?.id || u?._id || null;
  }, []);

  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [filter, category]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      const response = await forumAPI.getThreads({
        category: category === 'all' ? null : category,
        page: 1, limit: 20, sort: filter,
      });
      setThreads(response.threads || []);
    } catch (error) {
      console.error('Error loading threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const isLikedByMe = (thread) =>
    Boolean(currentUserId && (thread.likedBy || []).some(id => String(id) === String(currentUserId)));

  const handleLike = async (thread) => {
    if (!currentUserId) { alert('Please log in to like threads'); return; }
    if (likeBusyId) return;

    const id = thread._id;
    const liked = isLikedByMe(thread);
    setLikeBusyId(id);

    // optimistic toggle
    setThreads(prev => prev.map(t => {
      if (t._id !== id) return t;
      const likedBy = liked
        ? (t.likedBy || []).filter(u => String(u) !== String(currentUserId))
        : [...(t.likedBy || []), currentUserId];
      return {
        ...t,
        likedBy,
        stats: { ...t.stats, likes: Math.max(0, (t.stats?.likes || 0) + (liked ? -1 : 1)) },
      };
    }));

    try {
      const res = await forumAPI.likeThread(id);
      // reconcile with the server's authoritative count
      setThreads(prev => prev.map(t => {
        if (t._id !== id) return t;
        const likedBy = res?.liked
          ? [...new Set([...(t.likedBy || []).map(String), String(currentUserId)])]
          : (t.likedBy || []).filter(u => String(u) !== String(currentUserId));
        return {
          ...t,
          likedBy,
          stats: { ...t.stats, likes: typeof res?.likes === 'number' ? res.likes : t.stats?.likes },
        };
      }));
    } catch (error) {
      console.error('Error liking thread:', error);
      // roll back
      setThreads(prev => prev.map(t => {
        if (t._id !== id) return t;
        const likedBy = liked
          ? [...new Set([...(t.likedBy || []).map(String), String(currentUserId)])]
          : (t.likedBy || []).filter(u => String(u) !== String(currentUserId));
        return {
          ...t,
          likedBy,
          stats: { ...t.stats, likes: Math.max(0, (t.stats?.likes || 0) + (liked ? 1 : -1)) },
        };
      }));
    } finally {
      setLikeBusyId(null);
    }
  };

  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = threads.filter(thread => {
      if (!q) return true;
      return thread.title?.toLowerCase().includes(q) ||
             thread.content?.toLowerCase().includes(q) ||
             thread.authorUsername?.toLowerCase().includes(q);
    });

    // "Trending" badge is derived from real engagement: unpinned threads active in
    // the last week, ranked by likes + replies, top 2 only.
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const score = (t) => (t.stats?.likes || 0) + (t.stats?.replies || 0) * 2;
    const hot = list
      .filter(t => !t.isPinned && score(t) > 0)
      .filter(t => Date.now() - new Date(t.stats?.lastActivityAt || t.createdAt) < WEEK)
      .sort((a, b) => score(b) - score(a))
      .slice(0, 2)
      .map(t => t._id);

    return list.map(t => ({ ...t, _isTrending: hot.includes(t._id) }));
  }, [threads, searchQuery]);

  const totalReplies = useMemo(
    () => threads.reduce((sum, t) => sum + (t.stats?.replies || 0), 0),
    [threads]
  );

  return (
    <div style={{
      background: c.bg, color: c.text, fontFamily: MANROPE,
      minHeight: '100vh', width: '100%', overflowY: 'auto',
    }}>
      <style>{keyframes}</style>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: isMobile ? '20px 14px 80px' : '40px clamp(20px,5vw,56px) 80px' }}>

        {/* ── Header ── */}
        <section style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: isMobile ? 12 : 16, flexWrap: 'wrap', paddingBottom: isMobile ? 16 : 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, minWidth: 0, flex: isMobile ? '1 1 100%' : undefined }}>
            <div style={{
              width: isMobile ? 44 : 56, height: isMobile ? 44 : 56,
              borderRadius: isMobile ? 13 : 16, flexShrink: 0,
              background: `linear-gradient(135deg, ${c.purple}, ${c.pink})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(139,92,246,0.35)',
            }}>
              <MessageSquare size={isMobile ? 21 : 26} style={{ color: '#fff' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{
                fontFamily: SORA, fontWeight: 800,
                fontSize: 'clamp(24px,3.4vw,34px)', lineHeight: 1.15,
                margin: 0, letterSpacing: '-0.01em', color: c.text,
              }}>
                Community Forum
              </h1>
              <p style={{ fontSize: isMobile ? 12.5 : 14, color: c.textSub, margin: isMobile ? '4px 0 0' : '6px 0 0' }}>
                {loading
                  ? 'Loading discussions…'
                  : `${threads.length} ${threads.length === 1 ? 'discussion' : 'discussions'} · ${compact(totalReplies)} ${totalReplies === 1 ? 'reply' : 'replies'}`}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 28px rgba(139,92,246,0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(139,92,246,0.3)';
            }}
            style={{
              background: `linear-gradient(135deg, ${c.purple}, ${c.pink})`,
              color: '#fff', border: 'none', borderRadius: 12,
              padding: isMobile ? '12px 18px' : '13px 22px',
              fontSize: isMobile ? 14 : 14.5, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
              fontFamily: MANROPE,
              width: isMobile ? '100%' : undefined,
              boxShadow: '0 8px 20px rgba(139,92,246,0.3)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            <Plus size={16} /> New Thread
          </button>
        </section>

        {/* ── Sort + search ── */}
        <section style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingBottom: 16 }}>
          {SORT_FILTERS.map(f => {
            const Icon = f.icon;
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                style={{
                  background: active ? `linear-gradient(135deg, ${c.purple}, ${c.pink})` : c.cardFlat,
                  color: active ? '#fff' : '#c9c6d3',
                  border: active ? 'none' : `1px solid ${c.border}`,
                  borderRadius: 10, padding: '10px 16px',
                  fontSize: 13.5, fontWeight: active ? 700 : 600,
                  cursor: 'pointer', fontFamily: MANROPE,
                  display: 'flex', alignItems: 'center', gap: 7,
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                <Icon size={14} /> {f.label}
              </button>
            );
          })}

          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 180, position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={15} style={{ position: 'absolute', left: 14, color: c.textFaint }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search threads..."
              style={{
                width: '100%', background: c.cardFlat,
                border: `1px solid ${c.border}`, borderRadius: 999,
                padding: '11px 16px 11px 38px',
                fontSize: 14, color: c.text, fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>
        </section>

        {/* ── Categories ── */}
        <section style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: 24 }}>
          {CATEGORY_ORDER.map(cat => {
            const active = category === cat;
            const label = cat === 'all' ? 'All' : cfgFor(cat).label;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setCategory(cat)}
                style={{
                  background: '#141018',
                  color: active ? c.text : c.textMuted,
                  border: active ? `1.5px solid ${c.text}` : `1px solid ${c.border}`,
                  borderRadius: 999, padding: '8px 16px',
                  fontSize: 13.5, fontWeight: active ? 700 : 600,
                  cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: MANROPE,
                  transition: 'color 0.2s, border-color 0.2s',
                }}
              >
                {label}
              </button>
            );
          })}
        </section>

        {/* ── Threads ── */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{
                background: c.card, border: `1px solid ${c.border}`,
                borderRadius: 16, height: 96, animation: 'cf-pulse 1.5s infinite',
              }} />
            ))}
          </div>
        ) : filteredThreads.length === 0 ? (
          <section style={{
            textAlign: 'center', padding: '60px 20px',
            background: c.card, border: `1px solid ${c.border}`, borderRadius: 20,
          }}>
            <h2 style={{ fontFamily: SORA, fontWeight: 700, fontSize: 19, margin: 0, color: c.text }}>
              {searchQuery ? `No threads match "${searchQuery}"` : 'No threads here yet'}
            </h2>
            <p style={{ fontSize: 14, color: c.textSub, margin: '8px 0 0' }}>
              Try another search or category — or start the thread yourself.
            </p>
          </section>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredThreads.map((thread, idx) => (
              <div key={thread._id} style={{ animation: `cf-fadeUp 0.3s ease-out ${Math.min(idx, 8) * 0.03}s both` }}>
                <ThreadRow
                  thread={thread}
                  isHovered={hoveredThread === thread._id}
                  onHover={setHoveredThread}
                  onOpen={(slug) => onThreadClick && onThreadClick(slug)}
                  onLike={handleLike}
                  liked={isLikedByMe(thread)}
                  likeBusy={likeBusyId === thread._id}
                  isMobile={isMobile}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateThreadModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(thread) => {
          loadThreads();
          if (thread?.slug && onThreadClick) onThreadClick(thread.slug);
        }}
      />
    </div>
  );
};

export default Forum;
